import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Edit01Icon, LinkExternal01Icon } from "@vetty/icons";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Tabs,
  TabsItem,
} from "@/components";
import {
  formatAccountTimestamp,
  getAccountRoleMeta,
  getAccountSubscriptionMeta,
} from "../../_constants/accounts.const";
import { useAccountAdminDetailsQuery } from "../../_hooks/useAccountAdminDetailsQuery";
import {
  getSendPasswordResetErrorMessage,
  useAdminSendPasswordResetMutation,
} from "../../_hooks/useAdminSendPasswordResetMutation";
import {
  getResendDemoInviteErrorMessage,
  useAdminResendDemoInviteMutation,
} from "../../_hooks/useAdminResendDemoInviteMutation";
import type { Account } from "../../_types/accounts.types";
import {
  formatAdminDate,
  formatMoneyCents,
  humanizeInterval,
} from "../../_utils/formatAccountAdmin";
import { stripeCustomerDashboardUrl } from "../../_utils/stripeDashboardUrl";
import { EditAccountFieldDrawer, type AdminEditFieldKey } from "../edit-account-drawer";
import styles from "./AccountDetailsDrawer.module.scss";

type AccountDetailsDrawerProps = {
  account: Account | null;
  isOpen: boolean;
  onClose: () => void;
};

type DetailTab =
  | "account"
  | "access"
  | "security"
  | "subscription"
  | "invoices"
  | "activity";

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles["comp__section"]}>
      <div className={styles["comp__intro"]}>
        <h3 className={styles["comp__section-title"]}>{title}</h3>
        {subtitle ? <p className={styles["comp__subtitle"]}>{subtitle}</p> : null}
      </div>
      <div className={styles["comp__section-content"]}>{children}</div>
    </section>
  );
}

function MetaGrid({ children }: { children: React.ReactNode }) {
  return <div className={styles["comp__meta-grid"]}>{children}</div>;
}

function MetaItem({
  label,
  value,
  onEdit,
  editDisabled,
}: {
  label: string;
  value: React.ReactNode;
  onEdit?: () => void;
  editDisabled?: boolean;
}) {
  return (
    <div className={styles["comp__meta-item"]}>
      <div className={styles["comp__meta-label"]}>
        <span className={styles["comp__meta-label-text"]}>{label}</span>
        {onEdit ? (
          <button
            type="button"
            className={styles["comp__meta-edit"]}
            onClick={onEdit}
            disabled={editDisabled}
            aria-label={`Edit ${label}`}
          >
            <Edit01Icon style={{ width: 16, height: 16 }} />
          </button>
        ) : null}
      </div>
      <div className={styles["comp__meta-value"]}>{value}</div>
    </div>
  );
}

/** Clamp long cells; native title + keyboard focus for full text (no secret tokens). */
function ClampedText({ text }: { text: string }) {
  const value = text.trim().length > 0 ? text.trim() : "—";
  const interactive = value !== "—";
  return (
    <span
      className={styles["comp__clamp"]}
      title={interactive ? value : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      {value}
    </span>
  );
}

function formatSeatEffect(group: {
  seatLimit: number | null;
  seatOccupied: number;
  licenseStatus: string | null;
}): string {
  if (group.seatLimit == null) {
    return group.licenseStatus
      ? `License ${group.licenseStatus} · membership without seat cap`
      : "No seat pool · membership only";
  }
  return `Seats ${group.seatOccupied}/${group.seatLimit}${
    group.licenseStatus ? ` · ${group.licenseStatus}` : ""
  }`;
}

export const AccountDetailsDrawer = ({
  account,
  isOpen,
  onClose,
}: AccountDetailsDrawerProps) => {
  const [tab, setTab] = useState<DetailTab>("account");
  const [editField, setEditField] = useState<AdminEditFieldKey | null>(null);
  const [pwMessage, setPwMessage] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);
  const [demoMessage, setDemoMessage] = useState<string | null>(null);
  const [demoError, setDemoError] = useState<string | null>(null);
  const accountId = account?.id ?? null;
  const sendPwMutation = useAdminSendPasswordResetMutation();
  const resendDemoMutation = useAdminResendDemoInviteMutation();

  const { data, isLoading, isError, refetch, isFetching } = useAccountAdminDetailsQuery(
    accountId,
    isOpen,
  );

  useEffect(() => {
    setTab("account");
    setPwMessage(null);
    setPwError(null);
    setDemoMessage(null);
    setDemoError(null);
  }, [accountId]);

  useEffect(() => {
    if (!account) setEditField(null);
  }, [account]);

  const billing = data?.billing;
  const user = billing?.user;
  const billingUnavailable = billing?.availability === "not_configured";
  const subscription = billing?.subscription;
  const trialInfo = billing?.trialInfo;
  const paymentHistory = billing?.paymentHistory ?? [];
  const sessions = data?.sessions ?? [];
  const loginHistory = data?.loginHistory ?? [];
  const groups = data?.groups ?? [];

  const roleMeta = account ? getAccountRoleMeta(account.role) : null;
  const subscriptionMeta = account
    ? getAccountSubscriptionMeta(account.hasSubscription)
    : null;
  const fullName = account
    ? [account.firstName, account.lastName].filter(Boolean).join(" ").trim()
    : "";

  const loginRows = useMemo(() => {
    return loginHistory.map((row) => {
      const attemptedAt = Number(row.attemptedAt ?? row.attempted_at ?? 0);
      return {
        id: String(row.id ?? `${attemptedAt}-${row.ipAddress ?? row.ip_address}`),
        attemptedAt,
        status: String(row.status ?? "—"),
        ip: String(row.ipAddress ?? row.ip_address ?? "—"),
        country: String(row.country ?? "—"),
        city: row.city != null ? String(row.city) : "",
        userAgent: String(row.userAgent ?? row.user_agent ?? ""),
      };
    });
  }, [loginHistory]);

  const handleDetailsClose = useCallback(() => {
    if (editField !== null) {
      setEditField(null);
      return;
    }
    onClose();
  }, [editField, onClose]);

  const handleSendPasswordReset = useCallback(async () => {
    if (!account) return;
    setPwMessage(null);
    setPwError(null);
    try {
      await sendPwMutation.mutateAsync(account.id);
      setPwMessage("Password reset email has been sent (if the account is active).");
    } catch (e) {
      setPwError(getSendPasswordResetErrorMessage(e));
    }
  }, [account, sendPwMutation]);

  const handleResendDemoInvite = useCallback(async () => {
    if (!account) return;
    setDemoMessage(null);
    setDemoError(null);
    try {
      await resendDemoMutation.mutateAsync(account.id);
      setDemoMessage(
        "Demo invitation email has been sent (if the account is active). Link expires in 7 days.",
      );
    } catch (e) {
      setDemoError(getResendDemoInviteErrorMessage(e));
    }
  }, [account, resendDemoMutation]);

  if (!account) return null;

  const showLoading = isOpen && (isLoading || isFetching) && !data;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={handleDetailsClose}
        ariaLabel="Account details"
        closeOnOutsideClick={editField === null}
        closeOnEscape={editField === null}
      >
        <DrawerHeader
          title={
            <div className={styles["comp__title-row"]}>
              <h2 className={styles["comp__heading"]}>{fullName || account.email}</h2>
              {roleMeta ? (
                <span
                  className={classNames(
                    styles["comp__badge"],
                    styles[`comp__badge--${roleMeta.variant}`],
                  )}
                >
                  {roleMeta.label}
                </span>
              ) : null}
            </div>
          }
          actions={
            <>
              {user?.stripeCustomerId ? (
                <Button
                  href={stripeCustomerDashboardUrl(user.stripeCustomerId)}
                  openInNewTab
                  variant="secondary"
                  size="small"
                  startContent={
                    <LinkExternal01Icon style={{ width: 16, height: 16 }} />
                  }
                >
                  Open in Stripe
                </Button>
              ) : null}
            </>
          }
          onClose={handleDetailsClose}
        />
        <DrawerBody className={styles["comp__body"]}>
          <div className={styles["comp"]}>
          <div className={styles["comp__tabs-wrap"]}>
            <Tabs
              value={tab}
              onChange={(v) => setTab(v as DetailTab)}
              variant="secondary"
              size="medium"
            >
              <TabsItem value="account">Account</TabsItem>
              <TabsItem value="access">Access</TabsItem>
              <TabsItem value="security">Security</TabsItem>
              <TabsItem value="subscription">Subscription</TabsItem>
              <TabsItem value="invoices">Invoices</TabsItem>
              <TabsItem value="activity">Sign-in</TabsItem>
            </Tabs>
          </div>

          {isError ? (
            <div className={styles["comp__state"]}>
              <p>Could not load full account details.</p>
              <Button
                type="button"
                size="small"
                variant="secondary"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </div>
          ) : null}

          {showLoading ? (
            <div className={styles["comp__state"]}>
              <div className={styles["comp__spinner"]} />
              <span>Loading details…</span>
            </div>
          ) : null}

          {!showLoading && !isError && tab === "account" ? (
            <div className={styles["comp__scroll"]}>
              <Section
                title="Identity & status"
                subtitle="Profile fields, role, and account status."
              >
                <MetaGrid>
                  <MetaItem
                    label="First name"
                    value={(user?.firstName ?? account.firstName) || "—"}
                    onEdit={() => setEditField("firstName")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Last name"
                    value={(user?.lastName ?? account.lastName) || "—"}
                    onEdit={() => setEditField("lastName")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Email"
                    value={user?.email ?? account.email}
                    onEdit={() => setEditField("email")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Username"
                    value={
                      user?.username && user.username.length > 0 ? user.username : "—"
                    }
                    onEdit={() => setEditField("username")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Phone"
                    value={
                      user?.phoneNumber && user.phoneNumber.length > 0
                        ? user.phoneNumber
                        : "—"
                    }
                    onEdit={() => setEditField("phoneNumber")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Country"
                    value={
                      user?.country && user.country.length > 0 ? user.country : "—"
                    }
                    onEdit={() => setEditField("country")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Email verified"
                    value={
                      user?.emailVerified === true
                        ? "Yes"
                        : user?.emailVerified === false
                          ? "No"
                          : "—"
                    }
                    onEdit={() => setEditField("emailVerified")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Phone verified"
                    value={
                      user?.phoneVerified === true
                        ? "Yes"
                        : user?.phoneVerified === false
                          ? "No"
                          : "—"
                    }
                    onEdit={() => setEditField("phoneVerified")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Account type"
                    value={user?.accountType ? String(user.accountType) : "—"}
                    onEdit={() => setEditField("accountType")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Role"
                    value={user?.role ? String(user.role) : account.role}
                    onEdit={() => setEditField("role")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Account active"
                    value={user?.isActive !== false ? "Yes" : "No"}
                    onEdit={() => setEditField("isActive")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Onboarding completed"
                    value={
                      user?.onboardingCompleted === true
                        ? "Yes"
                        : user?.onboardingCompleted === false
                          ? "No"
                          : "—"
                    }
                    onEdit={() => setEditField("onboardingCompleted")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="Trial used"
                    value={
                      user?.trialUsed === true
                        ? "Yes"
                        : user?.trialUsed === false
                          ? "No"
                          : "—"
                    }
                    onEdit={() => setEditField("trialUsed")}
                    editDisabled={!user}
                  />
                  <MetaItem
                    label="UTM source"
                    value={
                      user?.utmSource && user.utmSource.length > 0
                        ? user.utmSource
                        : "—"
                    }
                  />
                  <MetaItem
                    label="Created at"
                    value={formatAccountTimestamp(
                      user?.createdAt ?? account.createdAt,
                    )}
                  />
                  <MetaItem
                    label="Last activity (list)"
                    value={formatAccountTimestamp(account.lastActivityAt)}
                  />
                </MetaGrid>
              </Section>

              <Section title="Billing identifiers">
                <MetaGrid>
                  <MetaItem
                    label="Stripe customer"
                    value={
                      user?.stripeCustomerId ? (
                        <span className={styles["comp__mono"]}>
                          {user.stripeCustomerId}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <MetaItem
                    label="List subscription ref."
                    value={
                      account.subscriptionId ? (
                        <span className={styles["comp__mono"]}>
                          {account.subscriptionId}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <MetaItem
                    label="Has active subscription (list)"
                    value={
                      subscriptionMeta ? (
                        <span
                          className={classNames(
                            styles["comp__badge"],
                            styles[`comp__badge--${subscriptionMeta.variant}`],
                          )}
                        >
                          {subscriptionMeta.label}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                </MetaGrid>
              </Section>
            </div>
          ) : null}

          {!showLoading && !isError && tab === "access" ? (
            <div className={styles["comp__scroll"]}>
              <Section
                title="Groups & license effect"
                subtitle="All memberships for this account. Seat capacity applies only when a pool license exists."
              >
                {groups.length === 0 ? (
                  <p className={styles["comp__muted"]}>
                    Not a member of any group.
                  </p>
                ) : (
                  <ul className={styles["comp__group-list"]}>
                    {groups.map((group) => (
                      <li key={group.id} className={styles["comp__group-card"]}>
                        <p className={styles["comp__group-title"]}>
                          {group.name}
                          {group.archived ? " (archived)" : ""}
                        </p>
                        <p className={styles["comp__group-meta"]}>
                          {group.kind} · {formatSeatEffect(group)}
                        </p>
                        <a className={styles["comp__link"]} href="/groups">
                          Manage groups
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>
          ) : null}

          {!showLoading && !isError && tab === "security" ? (
            <div className={styles["comp__scroll"]}>
              <Section
                title="Password reset"
                subtitle="Same email as Forgot password — 1 hour set-password link."
              >
                <p className={styles["comp__pw-hint"]}>
                  Requires transactional email (Resend or Gmail OAuth). Failures report
                  clearly when mail is not configured.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  disabled={!user || sendPwMutation.isPending}
                  loading={sendPwMutation.isPending}
                  onClick={() => void handleSendPasswordReset()}
                >
                  Send password reset email
                </Button>
                {pwMessage ? (
                  <p className={styles["comp__success"]}>{pwMessage}</p>
                ) : null}
                {pwError ? (
                  <p className={styles["comp__error-inline"]}>{pwError}</p>
                ) : null}
              </Section>

              <Section
                title="Demo invitation"
                subtitle="Resends the create-account setup link (7 days)."
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  disabled={!user || resendDemoMutation.isPending}
                  loading={resendDemoMutation.isPending}
                  onClick={() => void handleResendDemoInvite()}
                >
                  Resend demo invitation
                </Button>
                {demoMessage ? (
                  <p className={styles["comp__success"]}>{demoMessage}</p>
                ) : null}
                {demoError ? (
                  <p className={styles["comp__error-inline"]}>{demoError}</p>
                ) : null}
              </Section>
            </div>
          ) : null}

          {!showLoading && !isError && tab === "subscription" ? (
            <div className={styles["comp__scroll"]}>
              {billingUnavailable ? (
                <Section title="Plan & status">
                  <p className={styles["comp__muted"]}>
                    Billing is not configured in this environment. Subscription details
                    are unavailable — this does not mean the account has no
                    subscription.
                  </p>
                </Section>
              ) : (
                <Section title="Plan & status">
                  {subscription?.hasSubscription ? (
                    <MetaGrid>
                      <MetaItem
                        label="Product"
                        value={
                          subscription.productName &&
                          subscription.productName.length > 0
                            ? subscription.productName
                            : "—"
                        }
                      />
                      <MetaItem label="Status" value={subscription.status ?? "—"} />
                      <MetaItem
                        label="Price"
                        value={
                          subscription.unitAmount != null
                            ? `${formatMoneyCents(subscription.unitAmount, subscription.currency)} / ${humanizeInterval(subscription.priceInterval)}`
                            : "—"
                        }
                      />
                      <MetaItem
                        label="Billing (from API)"
                        value={subscription.billingCycle ?? "—"}
                      />
                      <MetaItem
                        label="Current period"
                        value={`${formatAdminDate(subscription.currentPeriodStart)} → ${formatAdminDate(subscription.currentPeriodEnd)}`}
                      />
                      <MetaItem
                        label="Renews / ends (period end)"
                        value={formatAdminDate(subscription.currentPeriodEnd)}
                      />
                      <MetaItem
                        label="Cancel at period end"
                        value={
                          subscription.cancelAtPeriodEnd === true
                            ? "Yes"
                            : subscription.cancelAtPeriodEnd === false
                              ? "No"
                              : "—"
                        }
                      />
                      <MetaItem
                        label="Stripe subscription"
                        value={
                          subscription.stripeSubscriptionId ? (
                            <span className={styles["comp__mono"]}>
                              {subscription.stripeSubscriptionId}
                            </span>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <MetaItem
                        label="Plan / product id"
                        value={
                          subscription.planId ? (
                            <span className={styles["comp__mono"]}>
                              {subscription.planId}
                            </span>
                          ) : (
                            "—"
                          )
                        }
                      />
                    </MetaGrid>
                  ) : (
                    <p className={styles["comp__muted"]}>
                      No Stripe subscription on file.
                    </p>
                  )}
                </Section>
              )}

              {!billingUnavailable ? (
                <>
                  <Section title="Trial">
                    <MetaGrid>
                      <MetaItem
                        label="Trial on account"
                        value={
                          trialInfo?.hasTrial
                            ? trialInfo.trialExpired
                              ? "Ended"
                              : "Active / available"
                            : "—"
                        }
                      />
                      <MetaItem
                        label="Trial window"
                        value={
                          trialInfo?.trialStart != null && trialInfo?.trialEnd != null
                            ? `${formatAdminDate(trialInfo.trialStart)} → ${formatAdminDate(trialInfo.trialEnd)}`
                            : "—"
                        }
                      />
                      <MetaItem
                        label="Days remaining"
                        value={
                          trialInfo?.daysRemaining != null && !trialInfo.trialExpired
                            ? String(trialInfo.daysRemaining)
                            : "—"
                        }
                      />
                      <MetaItem
                        label="Account trial flag (DB)"
                        value={
                          user?.trialUsed === true
                            ? "Used"
                            : user?.trialUsed === false
                              ? "Not used"
                              : "—"
                        }
                      />
                    </MetaGrid>
                  </Section>

                  <Section title="Discount / promo">
                    <MetaGrid>
                      <MetaItem
                        label="Promotion code"
                        value={
                          subscription?.promotionCode &&
                          subscription.promotionCode.length > 0
                            ? subscription.promotionCode
                            : "—"
                        }
                      />
                      <MetaItem
                        label="Coupon"
                        value={
                          subscription?.couponId ? (
                            <span className={styles["comp__mono"]}>
                              {subscription.couponId}
                            </span>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <MetaItem
                        label="Percent off"
                        value={
                          subscription?.couponPercentOff != null
                            ? `${subscription.couponPercentOff}%`
                            : "—"
                        }
                      />
                      <MetaItem
                        label="Amount off"
                        value={
                          subscription?.couponAmountOff != null
                            ? formatMoneyCents(
                                subscription.couponAmountOff,
                                subscription.currency,
                              )
                            : "—"
                        }
                      />
                    </MetaGrid>
                  </Section>

                  <Section title="Payment methods">
                    {billing?.paymentMethods && billing.paymentMethods.length > 0 ? (
                      <ul className={styles["comp__card-list"]}>
                        {billing.paymentMethods.map((pm) => (
                          <li key={pm.id} className={styles["comp__card-line"]}>
                            <span className={styles["comp__mono"]}>
                              {pm.brand ?? pm.type}
                            </span>
                            {pm.last4 ? ` ·•••${pm.last4}` : null}
                            {pm.expMonth != null && pm.expYear != null
                              ? ` · exp ${pm.expMonth}/${pm.expYear}`
                              : null}
                            {pm.isDefault ? (
                              <span className={styles["comp__pill"]}> Default</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles["comp__muted"]}>
                        No cards on file in Stripe.
                      </p>
                    )}
                  </Section>
                </>
              ) : null}
            </div>
          ) : null}

          {!showLoading && !isError && tab === "invoices" ? (
            <div className={styles["comp__scroll"]}>
              <Section title="Invoices (Stripe)">
                {billingUnavailable ? (
                  <p className={styles["comp__muted"]}>
                    Billing is not configured in this environment. Invoices are
                    unavailable — this does not mean the account has no invoices.
                  </p>
                ) : paymentHistory.length === 0 ? (
                  <p className={styles["comp__muted"]}>No invoices found.</p>
                ) : (
                  <div className={styles["comp__table-wrap"]}>
                    <table className={styles["comp__table"]}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Number</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {paymentHistory.map((inv) => (
                          <tr key={inv.id}>
                            <td>
                              <ClampedText text={formatAdminDate(inv.created)} />
                            </td>
                            <td>
                              <ClampedText
                                text={formatMoneyCents(inv.amount, inv.currency)}
                              />
                            </td>
                            <td>
                              <ClampedText text={inv.status} />
                            </td>
                            <td>
                              <ClampedText text={inv.number ?? "—"} />
                            </td>
                            <td className={styles["comp__table-actions"]}>
                              {inv.hostedInvoiceUrl ? (
                                <a
                                  href={inv.hostedInvoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles["comp__link"]}
                                >
                                  View
                                </a>
                              ) : null}
                              {inv.invoicePdf ? (
                                <a
                                  href={inv.invoicePdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles["comp__link"]}
                                >
                                  PDF
                                </a>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </div>
          ) : null}

          {!showLoading && !isError && tab === "activity" ? (
            <div className={styles["comp__scroll"]}>
              <Section
                title="Login history"
                subtitle="Long client strings clamp to two lines; full text on hover or keyboard focus."
              >
                {loginRows.length === 0 ? (
                  <p className={styles["comp__muted"]}>
                    No login attempts recorded.
                  </p>
                ) : (
                  <div className={styles["comp__table-wrap"]}>
                    <table className={styles["comp__table"]}>
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Status</th>
                          <th>IP</th>
                          <th>Location</th>
                          <th>Client</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginRows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <ClampedText
                                text={
                                  row.attemptedAt
                                    ? formatAdminDate(row.attemptedAt)
                                    : "—"
                                }
                              />
                            </td>
                            <td>
                              <ClampedText text={row.status} />
                            </td>
                            <td className={styles["comp__mono"]}>
                              <ClampedText text={row.ip} />
                            </td>
                            <td>
                              <ClampedText
                                text={
                                  [row.city, row.country].filter(Boolean).join(", ") ||
                                  "—"
                                }
                              />
                            </td>
                            <td>
                              <ClampedText text={row.userAgent || "—"} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="Active sessions">
                {sessions.length === 0 ? (
                  <p className={styles["comp__muted"]}>No active sessions.</p>
                ) : (
                  <div className={styles["comp__table-wrap"]}>
                    <table className={styles["comp__table"]}>
                      <thead>
                        <tr>
                          <th>Created</th>
                          <th>Last used</th>
                          <th>Expires</th>
                          <th>IP</th>
                          <th>Client</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((s) => (
                          <tr key={s.id}>
                            <td>
                              <ClampedText
                                text={formatAccountTimestamp(s.createdAt)}
                              />
                            </td>
                            <td>
                              <ClampedText
                                text={formatAccountTimestamp(s.lastUsedAt)}
                              />
                            </td>
                            <td>
                              <ClampedText
                                text={formatAccountTimestamp(s.expiresAt)}
                              />
                            </td>
                            <td className={styles["comp__mono"]}>
                              <ClampedText text={s.ipAddress || "—"} />
                            </td>
                            <td>
                              <ClampedText text={s.userAgent || "—"} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </div>
          ) : null}
          </div>
        </DrawerBody>
      </Drawer>
      <EditAccountFieldDrawer
        accountId={account.id}
        field={editField}
        isOpen={editField !== null}
        onClose={() => setEditField(null)}
        billingUser={user}
        billingLoaded={Boolean(data && !isError)}
      />
    </>
  );
};
